import React from 'react';
import { useStats, useFunnel, useSessions } from '../hooks/useData';
import { LiveEvent } from '../types';
import { formatDistanceToNow } from 'date-fns';
import { TrendingUp, Users, Flame, Zap } from 'lucide-react';

interface Props {
  events:   LiveEvent[];
  setPage:  (p: string) => void;
}

const FUNNEL_COLORS = [
  '#6366f1','#0ea5e9','#22c55e','#f59e0b','#a855f7','#ef4444'
];

export function Overview({ events, setPage }: Props) {
  const { stats }   = useStats();
  const { funnel }  = useFunnel();
  const { sessions } = useSessions();

  const topSessions = sessions.slice(0, 5);

  return (
    <div className="page">
      {/* ── Stats ─────────────────────────────────────────────────────── */}
      <div className="stats-grid">
        <StatCard
          label="Total Sessions"
          value={stats?.totalSessions ?? 0}
          sub="all time"
          icon={<Users size={16} />}
          color="var(--blue)"
        />
        <StatCard
          label="Active (5m)"
          value={stats?.activeLast5min ?? 0}
          sub="right now"
          icon={<Zap size={16} />}
          color="var(--amber)"
        />
        <StatCard
          label="Hot Leads"
          value={stats?.hotLeads ?? 0}
          sub="with consent"
          icon={<Flame size={16} />}
          color="var(--green)"
        />
        <StatCard
          label="Avg Intent"
          value={stats?.avgIntentScore ?? 0}
          sub="score / 100"
          icon={<TrendingUp size={16} />}
          color="var(--purple)"
        />
      </div>

      <div className="grid2" style={{ marginBottom: 16 }}>
        {/* ── Funnel ───────────────────────────────────────────────────── */}
        <div className="card">
          <div className="card-title">Conversion Funnel</div>
          {funnel.length === 0 ? (
            <Empty text="No funnel data yet" />
          ) : (
            funnel.map((stage, i) => (
              <div className="funnel-row" key={stage.stage}>
                <span className="funnel-label">{stage.stage}</span>
                <div className="funnel-bar-wrap">
                  <div
                    className="funnel-bar-fill"
                    style={{
                      width: `${stage.pct}%`,
                      background: FUNNEL_COLORS[i] ?? '#6366f1',
                      color: '#fff',
                    }}
                  >
                    {stage.pct}%
                  </div>
                </div>
                <span className="funnel-count">{stage.count}</span>
              </div>
            ))
          )}
        </div>

        {/* ── Live events ──────────────────────────────────────────────── */}
        <div className="card">
          <div className="flex justify-between items-center mb-4" style={{ marginBottom: 12 }}>
            <div className="card-title" style={{ marginBottom: 0 }}>Live Events</div>
            <button className="btn" style={{ padding: '4px 10px', fontSize: 11 }} onClick={() => setPage('live')}>
              View all
            </button>
          </div>
          <div className="live-feed">
            {events.length === 0 ? (
              <Empty text="Waiting for visitors..." />
            ) : (
              events.slice(0, 8).map(ev => (
                <FeedItem key={ev.id} event={ev} />
              ))
            )}
          </div>
        </div>
      </div>

      {/* ── Recent sessions ──────────────────────────────────────────────── */}
      <div className="card">
        <div className="flex justify-between items-center" style={{ marginBottom: 12 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>Recent Sessions</div>
          <button className="btn" style={{ padding: '4px 10px', fontSize: 11 }} onClick={() => setPage('sessions')}>
            View all
          </button>
        </div>
        {topSessions.length === 0 ? (
          <Empty text="No sessions yet — embed tracker.js on a website to start" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Visitor</th>
                  <th>Score</th>
                  <th>Source</th>
                  <th>Intent</th>
                  <th>Status</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {topSessions.map(s => (
                  <tr key={s.sessionId} onClick={() => setPage('sessions')}>
                    <td>
                      <div>{s.person ?? s.company ?? <span style={{ color: 'var(--text3)' }}>Anonymous</span>}</div>
                      {s.company && s.person && <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{s.company}</div>}
                    </td>
                    <td><ScorePill score={s.score} /></td>
                    <td className="mono" style={{ fontSize: 11 }}>{s.acquisition?.source ?? '—'}</td>
                    <td>
                      {s.pageIntent
                        ? <span className="badge badge-blue">{s.pageIntent}</span>
                        : <span className="mono" style={{ color: 'var(--text3)', fontSize: 11 }}>—</span>
                      }
                    </td>
                    <td>
                      {s.hasConsent
                        ? <span className="badge badge-green">🔥 Consented</span>
                        : s.hasOutreach
                        ? <span className="badge badge-purple">Outreached</span>
                        : <span className="badge badge-gray">Browsing</span>
                      }
                    </td>
                    <td className="mono" style={{ fontSize: 11, color: 'var(--text3)' }}>
                      {formatDistanceToNow(new Date(s.updatedAt), { addSuffix: true })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, icon, color }: any) {
  return (
    <div className="stat-card">
      <div className="flex justify-between items-center" style={{ marginBottom: 8 }}>
        <span className="stat-label">{label}</span>
        <span style={{ color, opacity: 0.8 }}>{icon}</span>
      </div>
      <div className="stat-value" style={{ color }}>{value}</div>
      <div className="stat-sub">{sub}</div>
    </div>
  );
}

function ScorePill({ score }: { score: number }) {
  const color = score >= 70 ? 'var(--green)' : score >= 40 ? 'var(--amber)' : 'var(--text3)';
  return (
    <span style={{ color, fontFamily: 'var(--mono)', fontSize: 13, fontWeight: 700 }}>
      {score}
    </span>
  );
}

function FeedItem({ event }: { event: LiveEvent }) {
  return (
    <div className="feed-item">
      <div className="feed-dot" style={{ background: event.color }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="feed-label truncate">{event.label}</div>
        {event.sublabel && <div className="feed-sub truncate">{event.sublabel}</div>}
      </div>
      <div className="feed-time">
        {formatDistanceToNow(new Date(event.ts), { addSuffix: true })}
      </div>
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return (
    <div className="empty">
      <div className="empty-title">{text}</div>
    </div>
  );
}
